/**
 * Telling the sign-in link which page it was followed from.
 *
 * The bar is one piece of markup for the whole site. It is rendered once by
 * `cached.html` and served from the edge to everybody, so the link cannot be
 * written with the reader's page in it: Hugo would put the first page it built
 * into all 14,000 of them. What knows the path is the browser, and this is the
 * only thing on the page that can ask it.
 *
 * With the script blocked the link is what the markup says, the sign-in carries
 * no return path, and the callback lands on the home page. That is the
 * arrangement everywhere else on this bar, and here it costs a reader one
 * journey back to what they were reading.
 *
 * Sign-out gets nothing of the kind, and cannot. Its form has to keep an empty
 * body, because the digest `src/cff/user-request.cff.js` puts on the request is
 * the one an empty body has. See "Three things about the edge that are easy to
 * get wrong" in `docs/user-features.md`.
 */

/** The link on the bar that starts a sign-in. */
const linkSelector = "a.cb-sign-in";

/** The parameter `SignInStart` reads a return path from. */
const returnParameter = "from";

/**
 * Points every sign-in link at the page it sits on.
 *
 * The path is taken from the browser rather than from the link, and put through
 * `URL` rather than concatenated, so what goes on the query is encoded the way
 * the Function URL decodes it. The handler checks the value again and refuses
 * anything that is not a path on this site, which is where that decision
 * belongs: this runs in the reader's browser and says nothing about what
 * reaches the route.
 *
 * The home page is left alone, since a sign-in that names nothing lands there
 * anyway.
 */
export function startSignInLink(document: Document, path: string): void {
  if (path === "/") {
    return;
  }

  for (const link of document.querySelectorAll<HTMLAnchorElement>(
    linkSelector,
  )) {
    const url = new URL(link.href);

    url.searchParams.set(returnParameter, path);
    link.setAttribute("href", `${url.pathname}${url.search}`);
  }
}
